import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import { notifications } from "@mantine/notifications";
import { imgNbaLogo } from "../../../../assets";
import { Endpoints } from "../../../../lib/endpoints";
import { PerformRequest } from "../../../../lib/usePerformRequest";
import { InlineLoader } from "../../../../components/Loader/LoadingOverlay";
import { ButtonLoader } from "../../../../components/Loader";

interface Branch {
  id: string;
  name: string;
}

interface BranchesResponse {
  results: Branch[];
}

export default function KYCStep1() {
  const navigate = useNavigate();
  const [selectedBranch, setSelectedBranch] = useState<string | null>(null);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchBranches();
  }, []);

  async function fetchBranches() {
    setIsLoading(true);
    try {
      const endpoint = Endpoints.GetBranches();
      const response = await PerformRequest<BranchesResponse>({
        method: endpoint.method,
        route: endpoint.route,
      });

      if (response.status === 200 && response.data) {
        const data = Array.isArray(response.data)
          ? { results: response.data }
          : response.data;
        setBranches(data.results || []);
      } else {
        // Fallback to default branches if API fails
        setBranches([
          { id: "ikorodu", name: "Ikorodu" },
          { id: "ikeja", name: "Ikeja" },
          { id: "lagos", name: "Lagos" },
          { id: "badagry", name: "Badagry" },
          { id: "others", name: "Others" },
        ]);
      }
    } catch (error: any) {
      // Fallback to default branches
      setBranches([
        { id: "ikorodu", name: "Ikorodu" },
        { id: "ikeja", name: "Ikeja" },
        { id: "lagos", name: "Lagos" },
        { id: "badagry", name: "Badagry" },
        { id: "others", name: "Others" },
      ]);
    } finally {
      setIsLoading(false);
    }
  }

  const handleNext = async () => {
    if (!selectedBranch) {
      notifications.show({
        title: "Validation Error",
        message: "Please select an NBA branch",
        color: "red",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const endpoint = Endpoints.SubmitKYCStep1();
      const response = await PerformRequest<{ success: boolean }>({
        method: endpoint.method,
        route: endpoint.route,
        data: {
          branch_id: selectedBranch,
        },
      });

      if (response.status === 200 || response.status === 201) {
        notifications.show({
          title: "Success",
          message: "Branch selected successfully",
          color: "green",
        });
        navigate("/d/kyc/step2");
      } else {
        notifications.show({
          title: "Error",
          message: "Failed to save branch selection. Please try again.",
          color: "red",
        });
      }
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: "Failed to save branch selection. Please try again.",
        color: "red",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="kyc-page">
      <div className="kyc-header">
        <div className="kyc-logo-section">
          <img className="kyc-logo-img" src={imgNbaLogo} alt="NBA Logo" />
          <div className="kyc-logo-text">
            <div className="kyc-logo-name">NIGERIAN BAR ASSOCIATION</div>
            <div className="kyc-logo-sub">REMUNERATION PORTAL</div>
          </div>
        </div>
      </div>

      <div className="kyc-progress">
        <button
          className="kyc-back-button"
          type="button"
          onClick={() => navigate("/d")}
          aria-label="Go back"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="kyc-progress-content">
          <span className="kyc-progress-text">Step 1 of 7</span>
          <div className="kyc-progress-bar">
            <div className="kyc-progress-fill" style={{ width: "14.3%" }} />
          </div>
        </div>
      </div>

      <div className="kyc-container">
        <div className="kyc-content">
          <h1 className="kyc-title">Select NBA Branch</h1>
          <p className="kyc-subtitle">Select your NBA branch.</p>

          {isLoading ? (
            <InlineLoader isLoading={true} text="Loading branches..." />
          ) : (
            <>
              <div className="kyc-branches">
                {branches.map((branch) => (
                  <button
                    key={branch.id}
                    className={`kyc-branch-card ${
                      selectedBranch === branch.id
                        ? "kyc-branch-card--selected"
                        : ""
                    }`}
                    type="button"
                    onClick={() => setSelectedBranch(branch.id)}
                    disabled={isSubmitting}
                  >
                    <div className="kyc-branch-icon">
                      {selectedBranch === branch.id ? (
                        <Check size={20} />
                      ) : (
                        <div className="kyc-branch-icon-placeholder" />
                      )}
                    </div>
                    <span className="kyc-branch-name">{branch.name}</span>
                  </button>
                ))}
              </div>

              <button
                className="dashboard-button dashboard-button--primary dashboard-button--large flex items-center justify-center gap-2"
                type="button"
                onClick={handleNext}
                disabled={!selectedBranch || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <ButtonLoader color="white" />
                    <span>Saving...</span>
                  </>
                ) : (
                  "Next"
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

